import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, Check, FileText, Loader2, Paperclip, Phone } from "lucide-react";
import { VerdeSpark } from "./ui-bits";
import { API, post } from "../lib/api";
import { leadSourceFields } from "../lib/leadSource";
import { measureLead } from "../lib/openaiPixel";
import { PHONE_DISPLAY, PHONE_TEL } from "./Navbar";
import { useLang } from "../i18n/LangContext";

const FIELD_KEYS = ["location", "projectType", "stage", "facilityRequested", "landValue", "projectCost",
  "planningStatus", "timeline", "exitStrategy", "companyName", "experience", "name", "email", "phone"];

const T = {
  en: {
    fieldLabels: {
      location: "Location", projectType: "Type", stage: "Stage", facilityRequested: "Facility requested",
      landValue: "Land value", projectCost: "Project cost", planningStatus: "Planning status",
      timeline: "Timeline", exitStrategy: "Exit", companyName: "Company", experience: "Track record",
      name: "Name", email: "Email", phone: "Phone",
    },
    checkItems: [
      { label: "Location — town or area", short: "Location", keys: ["location"] },
      { label: "Project type — apartments to hotels", short: "Project type", keys: ["projectType"] },
      { label: "Stage — site identified to pre-sales", short: "Stage", keys: ["stage"] },
      { label: "Land value", short: "Land value", keys: ["landValue"] },
      { label: "Total project cost", short: "Project cost", keys: ["projectCost"] },
      { label: "Facility requested", short: "Facility requested", keys: ["facilityRequested"] },
      { label: "Planning status & timeline", short: "Planning & timeline", keys: ["planningStatus", "timeline"] },
      { label: "Exit — bank facility or sale", short: "Exit", keys: ["exitStrategy"] },
      { label: "Company & track record", short: "Company & track record", keys: ["companyName", "experience"] },
      { label: "Your name & contact", short: "Name & contact", keys: ["name", "phone", "email"] },
    ],
    starters: [
      "Site under offer in Cascais — need €1.5m for the land",
      "Planning approved in Lisbon — funding before my bank comes in",
      "Pre-licensing in the Algarve — can Verde help?",
    ],
    inputPh: "What are you building, and where in Portugal?",
    inputHintA: "Enter",
    inputHintB: " to send · plans and appraisals welcome",
    attachAria: "Attach documents",
    sendAria: "Send",
    sendAriaChat: "Send message",
    loading: "Verde is opening the conversation…",
    orStartWith: "Or start with",
    attachNote: "Plans, brochures, appraisals or planning documents — attach any of them at any time.",
    callPre: "Prefer to speak with a human?",
    callCta: "Call",
    chatPh: "Reply to Verde…",
    uploading: "Uploading documents…",
    trustLine: "AI-assisted · Reviewed by a human underwriter · Not an offer of credit",
    panelTitle: "Underwriting case",
    buildingLive: "Building live",
    coveredSoFar: "Covered so far",
    documents: "Documents",
    docsEmpty: "None yet — attach plans, appraisals or planning documents any time.",
    summaryTitle: "Case summary",
    submitBtn: "Submit for underwriting",
    submittingBtn: "Submitting…",
    gatedNote: "Verde opens the submit button once the case has the essentials.",
    saved: (email) => `Saved — your case is on record and your indicative terms will come to ${email || "your email"}.`,
    saveAsk: "Save your case — Verde will send your indicative terms to your email.",
    saving: "Saving…",
    save: "Save",
    later: "Later",
    errAssistant: "The assistant is unavailable — please send that again.",
    errAssistantBubble: "Sorry — something went wrong on our side. Please send that again.",
    errBegin: "We couldn't start the conversation — please try again.",
    errUpload: "Upload failed — please try again.",
    errEmail: "Please enter a valid email address.",
    errSubmit: "Submission failed — please try again.",
    errMissing: (missing) => `A few details are still missing (${missing.join(", ")}). Tell Verde in the chat and it will add them.`,
    uploadedMsg: (n, names) => `I've uploaded ${n} document${n > 1 ? "s" : ""}: ${names}.`,
    missingNames: {},
  },
  pt: {
    fieldLabels: {
      location: "Localização", projectType: "Tipo", stage: "Fase", facilityRequested: "Financiamento pedido",
      landValue: "Valor do terreno", projectCost: "Custo do projeto", planningStatus: "Estado do licenciamento",
      timeline: "Prazo", exitStrategy: "Saída", companyName: "Empresa", experience: "Histórico",
      name: "Nome", email: "Email", phone: "Telefone",
    },
    checkItems: [
      { label: "Localização — cidade ou zona", short: "Localização", keys: ["location"] },
      { label: "Tipo de projeto — apartamentos a hotéis", short: "Tipo de projeto", keys: ["projectType"] },
      { label: "Fase — terreno identificado a pré-vendas", short: "Fase", keys: ["stage"] },
      { label: "Valor do terreno", short: "Valor do terreno", keys: ["landValue"] },
      { label: "Custo total do projeto", short: "Custo do projeto", keys: ["projectCost"] },
      { label: "Financiamento pedido", short: "Financiamento pedido", keys: ["facilityRequested"] },
      { label: "Licenciamento e prazo", short: "Licenciamento e prazo", keys: ["planningStatus", "timeline"] },
      { label: "Saída — crédito bancário ou venda", short: "Saída", keys: ["exitStrategy"] },
      { label: "Empresa e histórico", short: "Empresa e histórico", keys: ["companyName", "experience"] },
      { label: "Nome e contacto", short: "Nome e contacto", keys: ["name", "phone", "email"] },
    ],
    starters: [
      "Terreno em negociação em Cascais — preciso de €1,5m para a aquisição",
      "Licenciamento aprovado em Lisboa — financiamento antes do meu banco entrar",
      "Pré-licenciamento no Algarve — a Verde pode ajudar?",
    ],
    inputPh: "O que está a construir, e onde em Portugal?",
    inputHintA: "Enter",
    inputHintB: " para enviar · planos e avaliações são bem-vindos",
    attachAria: "Anexar documentos",
    sendAria: "Enviar",
    sendAriaChat: "Enviar mensagem",
    loading: "A Verde está a abrir a conversa…",
    orStartWith: "Ou comece com",
    attachNote: "Planos, brochuras, avaliações ou documentos de licenciamento — anexe qualquer um deles a qualquer momento.",
    callPre: "Prefere falar com uma pessoa?",
    callCta: "Ligue",
    chatPh: "Responda à Verde…",
    uploading: "A enviar documentos…",
    trustLine: "Com apoio de IA · Revisto por um subscritor humano · Não é uma oferta de crédito",
    panelTitle: "Processo de subscrição",
    buildingLive: "A construir em direto",
    coveredSoFar: "Coberto até agora",
    documents: "Documentos",
    docsEmpty: "Ainda sem documentos — anexe planos, avaliações ou documentos de licenciamento a qualquer momento.",
    summaryTitle: "Resumo do processo",
    submitBtn: "Submeter para subscrição",
    submittingBtn: "A submeter…",
    gatedNote: "A Verde abre o botão de submissão quando o processo tiver o essencial.",
    saved: (email) => `Guardado — o seu processo está registado e as suas condições indicativas serão enviadas para ${email || "o seu email"}.`,
    saveAsk: "Guarde o seu processo — a Verde enviará as suas condições indicativas para o seu email.",
    saving: "A guardar…",
    save: "Guardar",
    later: "Mais tarde",
    errAssistant: "A assistente está indisponível — envie novamente, por favor.",
    errAssistantBubble: "Desculpe — algo falhou do nosso lado. Envie novamente, por favor.",
    errBegin: "Não conseguimos iniciar a conversa — tente novamente, por favor.",
    errUpload: "Falha no envio — tente novamente, por favor.",
    errEmail: "Introduza um endereço de email válido.",
    errSubmit: "Falha na submissão — tente novamente, por favor.",
    errMissing: (missing) => `Ainda faltam alguns dados (${missing.join(", ")}). Diga à Verde na conversa e ela acrescenta-os.`,
    uploadedMsg: (n, names) => `Enviei ${n} documento${n > 1 ? "s" : ""}: ${names}.`,
    missingNames: {
      location: "localização", projectType: "tipo de projeto", stage: "fase",
      facilityRequested: "financiamento pedido", name: "nome", email: "email",
    },
  },
  nl: {
    fieldLabels: {
      location: "Locatie", projectType: "Type", stage: "Fase", facilityRequested: "Gevraagd krediet",
      landValue: "Grondwaarde", projectCost: "Projectkosten", planningStatus: "Planologische status",
      timeline: "Tijdsplan", exitStrategy: "Exit", companyName: "Bedrijf", experience: "Trackrecord",
      name: "Naam", email: "E-mail", phone: "Telefoon",
    },
    checkItems: [
      { label: "Locatie — stad of gebied", short: "Locatie", keys: ["location"] },
      { label: "Projecttype — van appartementen tot hotels", short: "Projecttype", keys: ["projectType"] },
      { label: "Fase — van grond tot voorverkoop", short: "Fase", keys: ["stage"] },
      { label: "Grondwaarde", short: "Grondwaarde", keys: ["landValue"] },
      { label: "Totale projectkosten", short: "Projectkosten", keys: ["projectCost"] },
      { label: "Gevraagd krediet", short: "Gevraagd krediet", keys: ["facilityRequested"] },
      { label: "Planologische status & tijdsplan", short: "Planning & tijdsplan", keys: ["planningStatus", "timeline"] },
      { label: "Exit — bankkrediet of verkoop", short: "Exit", keys: ["exitStrategy"] },
      { label: "Bedrijf & trackrecord", short: "Bedrijf & trackrecord", keys: ["companyName", "experience"] },
      { label: "Uw naam & contact", short: "Naam & contact", keys: ["name", "phone", "email"] },
    ],
    starters: [
      "Grond in optie in Cascais — ik heb €1,5 mln nodig voor de aankoop",
      "Vergunning goedgekeurd in Lissabon — financiering vóórdat mijn bank instapt",
      "Vergunningtraject in de Algarve — kan Verde helpen?",
    ],
    inputPh: "Wat bouwt u, en waar in Portugal?",
    inputHintA: "Enter",
    inputHintB: " om te versturen · plannen en taxaties zijn welkom",
    attachAria: "Documenten bijvoegen",
    sendAria: "Versturen",
    sendAriaChat: "Bericht versturen",
    loading: "Verde opent het gesprek…",
    orStartWith: "Of begin met",
    attachNote: "Plannen, brochures, taxaties of vergunningsdocumenten — voeg op elk moment toe wat u hebt.",
    callPre: "Liever met een mens praten?",
    callCta: "Bellen",
    chatPh: "Antwoord aan Verde…",
    uploading: "Documenten worden geüpload…",
    trustLine: "AI-ondersteund · Gecontroleerd door een menselijke underwriter · Geen kredietaanbod",
    panelTitle: "Beoordelingsdossier",
    buildingLive: "Live in opbouw",
    coveredSoFar: "Tot nu toe gedekt",
    documents: "Documenten",
    docsEmpty: "Nog geen documenten — voeg op elk moment plannen, taxaties of vergunningsdocumenten toe.",
    summaryTitle: "Dossiersamenvatting",
    submitBtn: "Indienen voor beoordeling",
    submittingBtn: "Indienen…",
    gatedNote: "Verde opent de indienknop zodra het dossier de essentie bevat.",
    saved: (email) => `Opgeslagen — uw dossier staat genoteerd en uw indicatieve voorwaarden gaan naar ${email || "uw e-mailadres"}.`,
    saveAsk: "Sla uw dossier op — Verde stuurt uw indicatieve voorwaarden naar uw e-mailadres.",
    saving: "Opslaan…",
    save: "Opslaan",
    later: "Later",
    errAssistant: "De assistent is niet beschikbaar — stuur het bericht opnieuw.",
    errAssistantBubble: "Sorry — aan onze kant ging iets mis. Stuur het bericht opnieuw.",
    errBegin: "We konden het gesprek niet starten — probeer het opnieuw.",
    errUpload: "Upload mislukt — probeer het opnieuw.",
    errEmail: "Voer een geldig e-mailadres in.",
    errSubmit: "Indienen mislukt — probeer het opnieuw.",
    errMissing: (missing) => `Er ontbreken nog enkele gegevens (${missing.join(", ")}). Zeg het tegen Verde in de chat, dan voegt het ze toe.`,
    uploadedMsg: (n, names) => `Ik heb ${n} document${n > 1 ? "en" : ""} geüpload: ${names}.`,
    missingNames: {
      location: "locatie", projectType: "projecttype", stage: "fase",
      facilityRequested: "gevraagd krediet", name: "naam", email: "e-mail",
    },
  },
};

const isDone = (item, f = {}) => item.keys.some((k) => !!f[k]);

function TickDot({ done, tone = "dark" }) {
  return (
    <span
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
        done
          ? "border-mint bg-mint"
          : tone === "light"
            ? "border-verde-700/35 bg-transparent"
            : "border-cream/30 bg-transparent"
      }`}
      aria-hidden="true"
    >
      {done && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 24 }}
        >
          <Check className="h-2.5 w-2.5 text-verde-950" strokeWidth={3.5} />
        </motion.span>
      )}
    </span>
  );
}

const formatSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`);

function Bubble({ m }) {
  if (m.role === "user") {
    return (
      <div className="ml-auto max-w-[85%]" data-testid="chat-msg-user">
        <div className="rounded-2xl rounded-br-md bg-verde-800 px-4 py-3 text-sm leading-relaxed text-white whitespace-pre-wrap">
          {m.content}
        </div>
        {m.attachments?.length > 0 && (
          <div className="mt-2 flex flex-wrap justify-end gap-2">
            {m.attachments.map((a) => (
              <span key={a} className="inline-flex items-center gap-1.5 rounded-full border border-sand bg-white px-3 py-1 text-[11px] text-charcoal">
                <FileText className="h-3 w-3 text-verde-700" /> {a}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="flex gap-3" data-testid="chat-msg-ai">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-verde-800 font-display text-sm font-black text-mint">V</span>
      <div className="max-w-[85%] rounded-2xl rounded-tl-md border border-sand bg-cream px-4 py-3 text-sm leading-relaxed text-charcoal whitespace-pre-wrap">
        {m.content}
      </div>
    </div>
  );
}

function Typing() {
  return (
    <div className="flex gap-3" data-testid="chat-typing">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-verde-800 font-display text-sm font-black text-mint">V</span>
      <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-md border border-sand bg-cream px-4 py-3.5">
        {[0, 150, 300].map((d) => (
          <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-verde-700" style={{ animationDelay: `${d}ms` }} />
        ))}
      </div>
    </div>
  );
}

export default function ApplyChat({ prefill, onSubmitted }) {
  const { lang } = useLang();
  const t = T[lang];
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [phase, setPhase] = useState("landing"); // landing | loading | chat
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [emailVal, setEmailVal] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailDone, setEmailDone] = useState(false);
  const [emailDismissed, setEmailDismissed] = useState(false);
  const emailKnownRef = useRef(!!prefill?.email);
  const threadRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamText, streaming]);

  useEffect(() => {
    if (phase !== "landing") return;
    try {
      if (window.matchMedia("(pointer: fine)").matches) inputRef.current?.focus();
    } catch {
      inputRef.current?.focus();
    }
  }, [phase]);

  const consumeSSE = async (sid, content, attachments) => {
    setMessages((m) => [...m, { role: "user", content, attachments }]);
    setInput("");
    setStreaming(true);
    setStreamText("");
    let acc = "";
    try {
      const res = await fetch(`${API}/underwriting/sessions/${sid}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, attachments }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n");
        buf = frames.pop();
        for (const frame of frames) {
          if (!frame.startsWith("data: ")) continue;
          const ev = JSON.parse(frame.slice(6));
          if (ev.type === "delta") {
            acc += ev.content;
            setStreamText(acc);
          } else if (ev.type === "state") {
            if (ev.fields?.email) emailKnownRef.current = true;
            setSession((s) => ({ ...s, fields: ev.fields, progress: ev.progress, ready_to_submit: ev.ready_to_submit, summary: ev.summary }));
          } else if (ev.type === "error") {
            setError(ev.message);
          }
        }
      }
      setMessages((m) => [...m, { role: "assistant", content: acc }]);
      if (!emailKnownRef.current && !emailDone && !emailDismissed) setShowEmailCapture(true);
    } catch {
      setError(t.errAssistant);
      setMessages((m) => [...m, { role: "assistant", content: t.errAssistantBubble }]);
    } finally {
      setStreaming(false);
      setStreamText("");
    }
  };

  const begin = async (text) => {
    setError("");
    setPhase("loading");
    try {
      const res = await post("/underwriting/sessions", { prefill: prefill || {}, lang });
      const s = res.data;
      emailKnownRef.current = !!s.fields?.email || !!prefill?.email;
      setSession(s);
      setMessages(s.messages || []);
      setDocs(s.documents || []);
      setPhase("chat");
      let attachments = [];
      if (pendingFiles.length) {
        setUploading(true);
        try {
          const fd = new FormData();
          pendingFiles.forEach((f) => fd.append("files", f));
          const up = await fetch(`${API}/underwriting/sessions/${s.session_id}/documents`, { method: "POST", body: fd });
          if (!up.ok) throw new Error("upload failed");
          const data = await up.json();
          setDocs(data.documents);
          attachments = pendingFiles.map((f) => f.name);
        } finally {
          setUploading(false);
          setPendingFiles([]);
        }
      }
      if (text.trim() || attachments.length) {
        await consumeSSE(s.session_id, text.trim() || t.uploadedMsg(attachments.length, attachments.join(", ")), attachments);
      }
    } catch {
      setPhase("landing");
      setError(t.errBegin);
    }
  };

  const autoStarted = useRef(false);
  useEffect(() => {
    if (prefill && !autoStarted.current) {
      autoStarted.current = true;
      begin("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFiles = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    if (!session) {
      setPendingFiles((p) => [...p, ...list]);
      return;
    }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      const up = await fetch(`${API}/underwriting/sessions/${session.session_id}/documents`, { method: "POST", body: fd });
      if (!up.ok) throw new Error("upload failed");
      const data = await up.json();
      setDocs(data.documents);
      await consumeSSE(session.session_id, t.uploadedMsg(list.length, list.map((f) => f.name).join(", ")), list.map((f) => f.name));
    } catch {
      setError(t.errUpload);
    } finally {
      setUploading(false);
    }
  };

  const submitCase = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await post(`/underwriting/sessions/${session.session_id}/submit`, leadSourceFields());
      measureLead(res?.data?.event_id);
      onSubmitted(res.data.reference, session.fields.name || "");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const missing = Array.isArray(detail?.missing) ? detail.missing : null;
      setError(
        missing
          ? t.errMissing(missing.map((k) => t.missingNames[k] || k))
          : t.errSubmit
      );
    } finally {
      setSubmitting(false);
    }
  };

  const saveEmail = async () => {
    if (!emailVal.trim() || !session) return;
    setEmailSaving(true);
    setError("");
    try {
      const res = await post(`/underwriting/sessions/${session.session_id}/email`, { email: emailVal.trim() });
      setSession((s) => ({ ...s, fields: res.data.fields }));
      setEmailDone(true);
      setShowEmailCapture(false);
    } catch (e) {
      setError(e?.response?.data?.detail || t.errEmail);
    } finally {
      setEmailSaving(false);
    }
  };

  const sendFrom = (target) => {
    if (phase !== "chat") {
      if (input.trim() || pendingFiles.length) begin(input);
      return;
    }
    if (streaming || uploading || !target.value.trim()) return;
    consumeSSE(session.session_id, target.value.trim(), []);
  };

  const fields = session?.fields || {};
  const progress = session?.progress || 0;
  const ready = session?.ready_to_submit;
  const filePicker = (testid) => (
    <input
      type="file"
      multiple
      className="hidden"
      data-testid={testid}
      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.zip"
      onChange={(e) => {
        onFiles(e.target.files);
        e.target.value = "";
      }}
    />
  );

  /* ── LANDING: AI-forward, Lovable-style ──────────────────────── */
  const landing = (
    <div className="mx-auto mt-8 max-w-2xl sm:mt-9" data-testid="apply-landing">
      <div className="relative rounded-3xl bg-verde-950 p-3 shadow-xl shadow-verde-950/20 ring-1 ring-mint/0 transition-shadow duration-300 focus-within:ring-2 focus-within:ring-mint/40 grain">
        <div className="flex items-end gap-2">
          <textarea
            rows={2}
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendFrom(e.target);
              }
            }}
            placeholder={t.inputPh}
            data-testid="apply-ai-input"
            className="flex-1 resize-none bg-transparent px-4 py-3 text-base text-cream outline-none placeholder:text-cream/40"
          />
          <div className="flex items-center gap-2 pb-1.5 pr-1">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label={t.attachAria}
              data-testid="apply-attach"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-cream/70 transition-colors hover:bg-white/10 hover:text-cream"
            >
              <Paperclip className="h-4.5 w-4.5" />
            </button>
            <button
              type="button"
              onClick={() => begin(input)}
              disabled={phase === "loading" || (!input.trim() && !pendingFiles.length)}
              aria-label={t.sendAria}
              data-testid="apply-send"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-mint text-verde-950 transition-all hover:bg-cream disabled:opacity-40"
            >
              {phase === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4.5 w-4.5" />}
            </button>
          </div>
        </div>
        <p className="px-4 pb-2 pt-1 text-[11px] text-cream/35" data-testid="apply-input-hint">
          <span className="font-medium text-cream/50">{t.inputHintA}</span>{t.inputHintB}
        </p>
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 pb-3 pt-1" data-testid="pending-files">
            {pendingFiles.map((f) => (
              <span key={f.name} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11px] text-cream">
                <FileText className="h-3 w-3 text-mint" /> {f.name}
              </span>
            ))}
          </div>
        )}
      </div>
      {phase === "loading" ? (
        <p className="mt-6 text-center text-sm text-fog" data-testid="landing-loading">{t.loading}</p>
      ) : (
        <>
          <p className="mt-14 text-[11px] font-semibold uppercase tracking-[0.18em] text-fog">{t.orStartWith}</p>
          <div className="mt-4 space-y-3" data-testid="starter-chips">
            {t.starters.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => { setInput(s); inputRef.current?.focus(); }}
                data-testid="starter-suggestion"
                className="group flex w-full items-center gap-3 rounded-2xl border border-sand bg-white px-4 py-3.5 text-left text-sm text-ink/80 transition-all duration-200 hover:-translate-y-px hover:border-verde-700/50 hover:shadow-md hover:shadow-verde-950/5 hover:text-verde-800"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-mint/15 transition-colors group-hover:bg-mint/25">
                  <VerdeSpark className="h-3.5 w-3.5 text-verde-700" />
                </span>
                {s}
                <ArrowUp className="ml-auto h-3.5 w-3.5 -rotate-45 text-fog/50 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-verde-700" />
              </button>
            ))}
          </div>
          <p className="mt-8 flex items-center justify-center gap-2 text-center text-xs text-fog" data-testid="landing-attach-note">
            <Paperclip className="h-3.5 w-3.5 shrink-0 text-verde-700" />
            {t.attachNote}
          </p>
          <div className="mt-14 flex flex-col items-center gap-2 border-t border-sand pt-8 text-center sm:flex-row sm:justify-center sm:gap-3" data-testid="landing-call-note">
            <p className="text-sm text-fog">
              {t.callPre}
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm">
              <Phone className="h-3.5 w-3.5 text-verde-700" />
              <span className="text-fog">{t.callCta}</span>
              <a
                href={PHONE_TEL}
                data-testid="landing-call-link"
                className="font-medium text-verde-800 underline decoration-verde-800/30 underline-offset-4 transition-colors hover:decoration-verde-800"
              >
                {PHONE_DISPLAY}
              </a>
            </span>
          </div>
        </>
      )}
      {error && <p className="mt-3 text-center text-sm text-red-700" data-testid="chat-error">{error}</p>}
      {filePicker("apply-file-input")}
    </div>
  );

  /* ── CHAT + LIVE CASE ─────────────────────────────────────────── */
  const chat = (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px] lg:gap-8" data-testid="apply-chat">
      <div className="flex h-[560px] flex-col rounded-2xl border border-sand bg-white lg:h-[640px]">
        {/* Mobile: live case-progress pill — pins to the top while the chat is on screen */}
        <div
          className="sticky top-2 z-20 mx-3 mt-3 flex items-center justify-between gap-3 rounded-full bg-verde-950/95 px-4 py-1.5 shadow-lg shadow-verde-950/25 lg:hidden"
          data-testid="case-progress-pill"
        >
          <span className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mint opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-mint" />
            </span>
            <span className="text-[11px] font-semibold text-cream">{t.buildingLive}</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="h-1 w-14 overflow-hidden rounded-full bg-white/15">
              <span className="block h-full rounded-full bg-mint transition-all duration-700" style={{ width: `${progress}%` }} />
            </span>
            <span className="font-mono text-[10px] tracking-[0.12em] text-mint">{progress}%</span>
          </span>
        </div>
        <div ref={threadRef} className="flex-1 space-y-5 overflow-y-auto px-4 py-6 sm:px-7" data-testid="chat-thread">
          {messages.map((m, i) => <Bubble key={i} m={m} />)}
          {streaming && (streamText ? <Bubble m={{ role: "assistant", content: streamText }} /> : <Typing />)}
          {uploading && <p className="text-center text-xs text-fog" data-testid="upload-progress">{t.uploading}</p>}
        </div>
        {showEmailCapture && (
          <div className="border-t border-sand bg-cream px-4 py-3 sm:px-7" data-testid="email-capture">
            {emailDone ? (
              <p className="flex items-center gap-2 text-xs font-medium text-verde-800" data-testid="email-capture-done">
                <Check className="h-3.5 w-3.5" /> {t.saved(fields.email)}
              </p>
            ) : (
              <>
                <p className="text-xs text-ink">{t.saveAsk}</p>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="email"
                    value={emailVal}
                    onChange={(e) => setEmailVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEmail(); } }}
                    placeholder="you@company.pt"
                    data-testid="email-capture-input"
                    className="min-w-0 flex-1 rounded-full border border-sand bg-white px-4 py-2 text-base text-charcoal outline-none placeholder:text-fog/60 focus:border-verde-700"
                  />
                  <button
                    type="button"
                    onClick={saveEmail}
                    disabled={emailSaving || !emailVal.trim()}
                    data-testid="email-capture-save"
                    className="shrink-0 rounded-full bg-verde-800 px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-verde-700 disabled:opacity-40"
                  >
                    {emailSaving ? t.saving : t.save}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEmailDismissed(true); setShowEmailCapture(false); }}
                    data-testid="email-capture-later"
                    className="shrink-0 text-xs font-medium text-fog underline underline-offset-2 hover:text-ink"
                  >
                    {t.later}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <div className="border-t border-sand p-3 sm:p-4">
          {error && <p className="mb-2 px-1 text-xs text-red-700" data-testid="chat-error">{error}</p>}
          <div className="flex items-end gap-2 rounded-xl border border-sand bg-cream px-2 py-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label={t.attachAria}
              data-testid="chat-attach"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink/60 transition-colors hover:bg-sand/60 hover:text-verde-800"
            >
              <Paperclip className="h-4 w-4" />
            </button>
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendFrom(e.target);
                }
              }}
              placeholder={t.chatPh}
              data-testid="chat-input"
              className="max-h-32 flex-1 resize-none bg-transparent px-1 py-2.5 text-base text-charcoal outline-none placeholder:text-fog/70"
            />
            <button
              type="button"
              onClick={() => sendFrom({ value: input })}
              disabled={streaming || uploading || !input.trim()}
              aria-label={t.sendAriaChat}
              data-testid="chat-send"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-verde-800 text-white transition-all hover:bg-verde-700 disabled:opacity-40"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-2 px-1 text-[10px] text-fog">{t.trustLine}</p>
          {filePicker("chat-file-input")}
        </div>
      </div>

      <aside className="h-fit rounded-2xl bg-verde-950 p-6 text-cream grain relative lg:sticky lg:top-24" data-testid="case-panel">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold text-white">{t.panelTitle}</h2>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-mint" data-testid="case-live">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-mint" /> {t.buildingLive}
          </span>
        </div>
        <div className="mt-5">
          <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-mint transition-all duration-700" style={{ width: `${progress}%` }} data-testid="case-progress" />
          </div>
          <p className="mt-2 text-right font-mono text-[10px] tracking-[0.14em] text-cream/50">{progress}%</p>
        </div>
        <div className="mt-4" data-testid="live-checklist">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cream/50">{t.coveredSoFar}</p>
          <ul className="mt-3 grid gap-x-5 gap-y-2 sm:grid-cols-2">
            {t.checkItems.map((item) => {
              const done = isDone(item, fields);
              return (
                <li key={item.short} className={`flex items-center gap-2 text-[11px] transition-colors duration-300 ${done ? "text-cream" : "text-cream/40"}`}>
                  <TickDot done={done} />
                  {item.short}
                </li>
              );
            })}
          </ul>
        </div>
        <dl className="mt-4 space-y-0" data-testid="case-fields">
          {FIELD_KEYS.map((key) => (
            <div key={key} className="flex items-baseline justify-between gap-4 border-b border-cream/10 py-2" data-testid={`case-field-${key}`}>
              <dt className="text-[11px] text-cream/50">{t.fieldLabels[key]}</dt>
              <dd className="max-w-[60%] truncate text-right text-xs font-medium text-cream" title={fields[key] || ""}>
                {fields[key] || "—"}
              </dd>
            </div>
          ))}
        </dl>
        <div className="mt-5" data-testid="case-docs">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cream/50">{t.documents}</p>
          {docs.length === 0 ? (
            <p className="mt-2 text-xs text-cream/40">{t.docsEmpty}</p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {docs.map((d) => (
                <li key={d.name} className="flex items-center gap-2 text-xs text-cream/85">
                  <FileText className="h-3 w-3 shrink-0 text-mint" /> <span className="truncate">{d.name}</span>
                  <span className="ml-auto shrink-0 text-cream/40">{formatSize(d.size)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {session?.summary && (
          <div className="mt-5 rounded-xl border border-cream/15 bg-white/5 p-4" data-testid="case-summary">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-mint">{t.summaryTitle}</p>
            <p className="mt-2 text-xs leading-relaxed text-cream/85">{session.summary}</p>
          </div>
        )}
        <button
          type="button"
          onClick={submitCase}
          disabled={!ready || submitting}
          className="group mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-mint px-7 py-3.5 text-sm font-semibold text-verde-950 transition-colors hover:bg-cream disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="case-submit"
        >
          {submitting ? t.submittingBtn : t.submitBtn}
        </button>
        {!ready && <p className="mt-3 text-center text-[10px] leading-relaxed text-cream/40">{t.gatedNote}</p>}
      </aside>
    </div>
  );

  return (
    <AnimatePresence mode="wait">
      {phase !== "chat" ? (
        <motion.div
          key="landing"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -14 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          {landing}
        </motion.div>
      ) : (
        <motion.div
          key="chat"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          {chat}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
